import React from 'react';
import { Helmet } from 'react-helmet-async';
import PackageCard from '../components/services/PackageCard';
import FeatureBox from '../components/ui/FeatureBox';
import ProcessStep from '../components/ui/ProcessStep';
import CTASection from '../components/ui/CTASection';
import FAQ from '../components/ui/FAQ';
import { socialMediaContent } from '../content/socialMedia';
import './SocialMedia.css';

const SocialMedia = () => {
  return (
    <>
      <Helmet>
        <title>إدارة السوشيال ميديا - نمو ماركتينج</title>
        <meta
          name="description"
          content="نُدير حساباتك بمحتوى جذاب ومميز يزيد تفاعل جمهورك ويبني علاقة قوية مع عملائك"
        />
      </Helmet>

      <div className="social-media-page">
        {/* Hero Section */}
        <section className="page-hero">
          <div className="container">
            <div className="page-hero-content">
              <h1 className="page-hero-title">{socialMediaContent.hero.title}</h1>
              <p className="page-hero-subtitle">{socialMediaContent.hero.subtitle}</p>
            </div>
          </div>
        </section>

        {/* Why You Need Social Media */}
        <section className="section">
          <div className="container">
            <div className="section-header">
              <h2 className="section-title">{socialMediaContent.whyNeed.title}</h2>
              <p className="section-subtitle">{socialMediaContent.whyNeed.subtitle}</p>
            </div>
            <div className="grid grid-4">
              {socialMediaContent.whyNeed.reasons.map((reason) => (
                <FeatureBox
                  key={reason.id}
                  icon={reason.icon}
                  title={reason.title}
                  description={reason.description}
                />
              ))}
            </div>
          </div>
        </section>

        {/* What We Offer */}
        <section className="section bg-light">
          <div className="container">
            <div className="section-header">
              <h2 className="section-title">{socialMediaContent.whatWeOffer.title}</h2>
            </div>
            <div className="grid grid-4">
              {socialMediaContent.whatWeOffer.features.map((feature) => (
                <FeatureBox
                  key={feature.id}
                  icon={feature.icon}
                  title={feature.title}
                  description={feature.description}
                />
              ))}
            </div>
          </div>
        </section>

        {/* ROI Insights */}
        <section className="section">
          <div className="container">
            <div className="section-header">
              <h2 className="section-title">{socialMediaContent.roiInsights.title}</h2>
              <p className="section-subtitle">{socialMediaContent.roiInsights.subtitle}</p>
            </div>
            <div className="grid grid-4">
              {socialMediaContent.roiInsights.insights.map((insight) => (
                <FeatureBox
                  key={insight.id}
                  icon={insight.icon}
                  title={insight.title}
                  description={insight.description}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Portfolio Section */}
        <section className="section portfolio-section bg-light">
          <div className="container">
            <div className="section-header">
              <h2 className="section-title">{socialMediaContent.portfolio.title}</h2>
              <p className="section-subtitle">{socialMediaContent.portfolio.subtitle}</p>
            </div>
            <div className="portfolio-grid">
              {socialMediaContent.portfolio.projects.map((project) => (
                <div key={project.id} className="portfolio-item">
                  <div className="portfolio-item-image">
                    <img src={project.image} alt={project.title} />
                    <div className="portfolio-item-overlay">
                      <div className="portfolio-item-overlay-content">
                        <span className="portfolio-item-category">{project.platform}</span>
                        <h3 className="portfolio-item-title">{project.title}</h3>
                        <p className="portfolio-item-description">{project.description}</p>
                        <div className="portfolio-item-features">
                          {project.metrics.map((metric, index) => (
                            <span key={index} className="portfolio-feature-tag">{metric}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Value Proposition */}
        <section className="section">
          <div className="container">
            <div className="section-header">
              <h2 className="section-title">{socialMediaContent.valueProposition.title}</h2>
              <p className="section-subtitle">{socialMediaContent.valueProposition.subtitle}</p>
            </div>
            <div className="grid grid-4">
              {socialMediaContent.valueProposition.comparisons.map((comparison) => (
                <FeatureBox
                  key={comparison.id}
                  icon={comparison.icon}
                  title={comparison.title}
                  description={comparison.description}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Packages */}
        <section className="section packages-section">
          <div className="container">
            <div className="section-header">
              <h2 className="section-title">{socialMediaContent.packagesSection.title}</h2>
              <p className="section-subtitle">{socialMediaContent.packagesSection.subtitle}</p>
            </div>
            <div className="packages-grid">
              {socialMediaContent.packages.map((pkg) => (
                <PackageCard
                  key={pkg.id}
                  title={pkg.title}
                  subtitle={pkg.subtitle}
                  features={pkg.features}
                  badge={pkg.badge}
                  isFeatured={pkg.isFeatured}
                  whatsappMessage={pkg.whatsappMessage}
                />
              ))}
            </div>
          </div>
        </section>

        {/* How We Work */}
        <section className="section bg-light">
          <div className="container">
            <div className="section-header">
              <h2 className="section-title">{socialMediaContent.process.title}</h2>
            </div>
            <div className="process-grid">
              {socialMediaContent.process.steps.map((step) => (
                <ProcessStep
                  key={step.id}
                  number={step.number}
                  icon={step.icon}
                  title={step.title}
                  description={step.description}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Success Stories */}
        <section className="section">
          <div className="container">
            <div className="section-header">
              <h2 className="section-title">{socialMediaContent.successStories.title}</h2>
            </div>
            <div className="grid grid-3">
              {socialMediaContent.successStories.stories.map((story) => {
                const StoryIcon = story.icon;
                return (
                  <div key={story.id} className="success-story-card">
                    <div className="story-icon">
                      <StoryIcon />
                    </div>
                    <h3 className="story-title">{story.title}</h3>
                    <p className="story-description">{story.description}</p>
                    <p className="story-result">{story.result}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <FAQ title={socialMediaContent.faq.title} items={socialMediaContent.faq.items} />

        {/* Final CTA */}
        <CTASection
          title={socialMediaContent.finalCTA.title}
          subtitle={socialMediaContent.finalCTA.subtitle}
          buttonText={socialMediaContent.finalCTA.buttonText}
          backgroundStyle="gradient"
        />
      </div>
    </>
  );
};

export default SocialMedia;
